import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import {
  ActivityItemType,
  SearchParticipantResult,
} from "../../../pages/createBulkActivity/modelsAndSchemas/Models";
import { TransferSearchParticipantResult } from "../../../pages/createBulkTransfer/modelsAndSchemas/Models";
import { BulkTransferSearchInputValues, ProgramModal } from "./models";

const initialState: ProgramModal = {
  programStatus: "",
  programDeliverableCountry: "",
  bulkActivitySelectedParticipants: [],
  bulkActivityProgramTemplateId: [],
  bulkActivityList: [],
  bulkTransferSelectedParticipants: [],
  bulkTransferSelectedStoreParticipants: [],
  bulkTransferSearchInputValues: {},
  clientId: "",
  programTemplateId: "",
  programCreatedDate: undefined,
  billingCountryId: "",
  areOpcoFieldsEditable: true,
};

const programSlice = createSlice({
  name: "programSlice",
  initialState,
  reducers: {
    updateProgramStatus: (state, action: PayloadAction<string>) => {
      state.programStatus = action.payload;
    },

    updateProgramCreatedDate: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.programCreatedDate = action.payload;
    },
    updateDeliverableCountry: (state, { payload }: PayloadAction<string>) => {
      state.programDeliverableCountry = payload;
    },
    setBulkActivitySelectedParticipants: (
      state,
      { payload }: PayloadAction<SearchParticipantResult[]>,
    ) => {
      state.bulkActivitySelectedParticipants = payload;
    },
    setBulkActivityProgramTemplateId: (
      state,
      { payload }: PayloadAction<string[]>,
    ) => {
      state.bulkActivityProgramTemplateId = payload;
    },

    updateParticipantDeletion: (
      state,
      { payload }: PayloadAction<{ id: string }>,
    ) => {
      state.bulkActivitySelectedParticipants =
        state.bulkActivitySelectedParticipants.map((participant) => {
          if (participant.participantId === payload.id) {
            return { ...participant, isSelected: false };
          }
          return participant;
        });
    },
    setBulkActivityList: (
      state,
      { payload }: PayloadAction<ActivityItemType[]>,
    ) => {
      state.bulkActivityList = payload;
    },
    updateActivityInBulkActivityList: (
      state,
      { payload }: PayloadAction<ActivityItemType>,
    ) => {
      state.bulkActivityList = state.bulkActivityList.map((activity) =>
        activity.id === payload.id ? payload : activity,
      );
    },
    setBulkTransferSelectedParticipants: (
      state,
      { payload }: PayloadAction<TransferSearchParticipantResult[]>,
    ) => {
      state.bulkTransferSelectedParticipants = payload;
    },
    setBulkTransferSelectedStoreParticipants: (
      state,
      { payload }: PayloadAction<TransferSearchParticipantResult[]>,
    ) => {
      state.bulkTransferSelectedStoreParticipants = payload;
    },
    setBulkTransferSearchInputValues: (
      state,
      { payload }: PayloadAction<BulkTransferSearchInputValues>,
    ) => {
      state.bulkTransferSearchInputValues = payload;
    },
    updateBulkTransferParticipantDeletion: (
      state,
      { payload }: PayloadAction<{ id: string }>,
    ) => {
      state.bulkTransferSelectedParticipants =
        state.bulkTransferSelectedParticipants.map((participant) => {
          if (participant.participantId === payload.id) {
            return { ...participant, isSelected: false };
          }
          return participant;
        });
    },
    updateBulkTransferStoreParticipantDeletion: (
      state,
      { payload }: PayloadAction<{ id: string }>,
    ) => {
      state.bulkTransferSelectedStoreParticipants =
        state.bulkTransferSelectedStoreParticipants.map((participant) => {
          if (participant.participantId === payload.id) {
            return { ...participant, isSelected: false };
          }
          return participant;
        });
    },
    updateProgramDetails: (
      state,
      {
        payload,
      }: PayloadAction<{
        clientId: string;
        programTemplateId: string;
        billingCountryId: string;
      }>,
    ) => {
      return {
        ...state,
        clientId: payload.clientId,
        programTemplateId: payload.programTemplateId,
        billingCountryId: payload.billingCountryId,
      };
    },
    updateOpcoFieldsEditable: (state, action: PayloadAction<boolean>) => {
      state.areOpcoFieldsEditable = action.payload;
    },
  },
});

export const {
  updateProgramStatus,
  updateDeliverableCountry,
  setBulkActivitySelectedParticipants,
  setBulkActivityProgramTemplateId,
  updateParticipantDeletion,
  setBulkActivityList,
  updateActivityInBulkActivityList,
  setBulkTransferSelectedParticipants,
  setBulkTransferSelectedStoreParticipants,
  setBulkTransferSearchInputValues,
  updateBulkTransferParticipantDeletion,
  updateBulkTransferStoreParticipantDeletion,
  updateProgramDetails,
  updateProgramCreatedDate,
  updateOpcoFieldsEditable,
} = programSlice.actions;

export const persistProgramConfig = {
  version: 1,
  key: "mpg_next_admin_program_v1",
  storage,
  whitelist: [],
};

export default programSlice.reducer;
